import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getSupportTicket,
  rateSupportTicket,
  resolveSupportTicket,
  sendSupportMessage,
  type SupportTicket,
} from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import { notifySupportUpdated } from '../../hooks/useSupportUnread';
import { getApiErrorMessage } from '../../utils/apiError';
import SupportRating from './SupportRating';
import { SupportStatusChip } from './SupportTicketList';
import { BODY_MAX, categoryLabel, formatSupportDate } from './supportLabels';
import './SupportPage.css';

const SupportTicketPage = () => {
  const { ticketId } = useParams();
  const id = Number(ticketId);
  const { isSupport } = useAuth();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();

  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');

  const load = useCallback(async () => {
    if (!Number.isFinite(id) || id <= 0) {
      setError('Обращение не найдено');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setTicket(await getSupportTicket(id));
      notifySupportUpdated();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить обращение'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSend = (event: FormEvent) => {
    event.preventDefault();
    const text = reply.trim();
    if (!text) return;
    void runSubmit(async () => {
      try {
        setTicket(await sendSupportMessage(id, text));
        setReply('');
        notifySupportUpdated();
      } catch (err) {
        showError(getApiErrorMessage(err, 'Не удалось отправить сообщение'));
      }
    });
  };

  const handleResolve = () => {
    void runSubmit(async () => {
      try {
        setTicket(await resolveSupportTicket(id));
        notifySupportUpdated();
        showSuccess('Обращение отмечено решённым');
      } catch (err) {
        showError(getApiErrorMessage(err, 'Не удалось отметить обращение'));
      }
    });
  };

  const handleRate = (rating: number) => {
    void runSubmit(async () => {
      try {
        setTicket(await rateSupportTicket(id, rating));
        showSuccess('Спасибо за оценку');
      } catch (err) {
        showError(getApiErrorMessage(err, 'Не удалось сохранить оценку'));
      }
    });
  };

  const backTo = isSupport ? '/support/inbox' : '/support';
  const backLabel = isSupport ? '← Входящие' : '← Мои обращения';

  return (
    <div className="support-page">
      {feedbackNode}
      <div className="support-container">
        <Link to={backTo} className="support-back">
          {backLabel}
        </Link>

        {loading && !ticket ? (
          <div className="support-state" role="status">
            Загрузка…
          </div>
        ) : error || !ticket ? (
          <div className="support-state support-state--error" role="alert">
            {error || 'Обращение не найдено'}
          </div>
        ) : (
          <>
            <section className="support-ticket-head ui-panel">
              <h2>{ticket.subject}</h2>
              <div className="support-ticket-head-row">
                <SupportStatusChip status={ticket.status} />
                <span>№ {ticket.id}</span>
                <span>{categoryLabel(ticket.category)}</span>
                <span>от {formatSupportDate(ticket.created_at)}</span>
              </div>
              {isSupport ? (
                <div className="support-context">
                  <p className="support-context-title">Контекст обращения</p>
                  <dl>
                    {ticket.author ? (
                      <>
                        <dt>Автор</dt>
                        <dd>
                          {ticket.author.name} ({ticket.author.login})
                        </dd>
                      </>
                    ) : null}
                    <dt>Клуб</dt>
                    <dd>{ticket.club_name || '—'}</dd>
                    <dt>Борт</dt>
                    <dd>{ticket.aircraft_tail || '—'}</dd>
                    <dt>Платформа</dt>
                    <dd>
                      {ticket.platform || '—'}
                      {ticket.app_version ? `, версия ${ticket.app_version}` : ''}
                    </dd>
                    <dt>Экран</dt>
                    <dd>{ticket.screen || '—'}</dd>
                  </dl>
                </div>
              ) : null}
            </section>

            <ol className="support-thread" aria-label="Переписка">
              {(ticket.messages || []).map((message) => {
                const mine = message.is_from_support === isSupport;
                return (
                  <li
                    key={message.id}
                    className={`support-msg ${mine ? 'support-msg--mine' : 'support-msg--theirs'}`}
                  >
                    <span className="support-msg-author">
                      {mine ? 'Вы' : message.is_from_support ? 'Поддержка' : 'Пользователь'}
                    </span>
                    <p className="support-msg-body">{message.body}</p>
                    <time className="support-msg-time" dateTime={message.created_at}>
                      {formatSupportDate(message.created_at)}
                    </time>
                  </li>
                );
              })}
            </ol>

            {ticket.status === 'resolved' ? (
              <div className="support-resolved ui-panel ui-panel-tight">
                <span>Вопрос решён</span>
                <SupportRating
                  rating={ticket.rating}
                  isSupport={isSupport}
                  submitting={submitting}
                  onRate={handleRate}
                />
                {isSupport ? null : (
                  <Link to="/support/new" className="support-btn support-btn--primary">
                    Новое обращение
                  </Link>
                )}
              </div>
            ) : (
              <form className="support-composer ui-panel ui-panel-tight" onSubmit={handleSend}>
                <label htmlFor="support-reply" className="support-visually-hidden">
                  Сообщение
                </label>
                <textarea
                  id="support-reply"
                  value={reply}
                  maxLength={BODY_MAX}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={isSupport ? 'Ответ пользователю' : 'Ваше сообщение'}
                />
                <div className="support-actions">
                  {isSupport ? (
                    <button
                      type="button"
                      className="support-btn"
                      onClick={handleResolve}
                      disabled={submitting}
                    >
                      Отметить решённым
                    </button>
                  ) : null}
                  <button
                    type="submit"
                    className="support-btn support-btn--primary"
                    disabled={submitting || reply.trim() === ''}
                  >
                    {submitting ? 'Отправка…' : 'Отправить'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupportTicketPage;
